// КРИТИЧЕСКАЯ ДИАГНОСТИКА: Browser-specific проблемы с Supabase
// Date: 2025-08-11
// Purpose: Выявить различия между браузерами для profiles запроса

// Narrow NetworkInformation interface for cross-browser typing
type BrowserNetworkInformation = {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  type?: string;
};

export const browserDiagnostic = {
  // 🔍 РАСШИРЕННАЯ ИНФОРМАЦИЯ О БРАУЗЕРЕ
  getBrowserInfo: () => {
    const userAgent = navigator.userAgent;
    const isChrome = /Chrome/.test(userAgent);
    const isChromium = /Chromium/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
    const isFirefox = /Firefox/.test(userAgent);

    const browserInfo = {
      userAgent,
      isChrome,
      isChromium,
      isSafari,
      isFirefox,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      language: navigator.language,
      languages: navigator.languages,
      platform: navigator.platform,
      vendor: navigator.vendor,
      hardwareConcurrency: navigator.hardwareConcurrency,
      maxTouchPoints: navigator.maxTouchPoints,
      webdriver: (("webdriver" in navigator) ? (navigator as unknown as { webdriver?: boolean }).webdriver : undefined),
      // 🌐 NETWORK CONNECTION INFO (если поддерживается)
      connection: null as BrowserNetworkInformation | null
    };

    // Добавляем navigator.connection если поддерживается
    if ('connection' in navigator) {
      const navWithConn = navigator as unknown as {
        connection?: BrowserNetworkInformation;
        mozConnection?: BrowserNetworkInformation;
        webkitConnection?: BrowserNetworkInformation;
      };
      const connection = navWithConn.connection || navWithConn.mozConnection || navWithConn.webkitConnection;
      if (connection) {
        browserInfo.connection = {
          effectiveType: connection.effectiveType,
          downlink: connection.downlink,
          rtt: connection.rtt,
          saveData: connection.saveData,
          type: connection.type
        };
        console.log('🔍 [NETWORK INFO] Connection details:', browserInfo.connection);
      }
    }
    
    // 📊 ЯВНОЕ ЛОГИРОВАНИЕ КЛЮЧЕВЫХ ПАРАМЕТРОВ
    console.log('🔍 [BROWSER INFO] Key details:', {
      userAgent: browserInfo.userAgent,
      platform: browserInfo.platform,
      language: browserInfo.language,
      isChrome: browserInfo.isChrome,
      isChromium: browserInfo.isChromium,
      vendor: browserInfo.vendor
    });
    
    return browserInfo;
  },

  // Проверка CORS и fetch capabilities
  checkCORSSupport: () => {
    return {
      fetchSupported: typeof fetch !== 'undefined',
      corsSupported: 'withCredentials' in new XMLHttpRequest(),
      credentialsSupported: 'credentials' in Request.prototype,
      abortControllerSupported: typeof AbortController !== 'undefined'
    };
  },

  // Проверка cookie и storage
  checkStorageSupport: () => {
    try {
      const testKey = '__supabase_test__';
      localStorage.setItem(testKey, 'test');
      const localStorageWorks = localStorage.getItem(testKey) === 'test';
      localStorage.removeItem(testKey);

      sessionStorage.setItem(testKey, 'test');
      const sessionStorageWorks = sessionStorage.getItem(testKey) === 'test';
      sessionStorage.removeItem(testKey);

      return {
        localStorage: localStorageWorks,
        sessionStorage: sessionStorageWorks,
        cookieEnabled: navigator.cookieEnabled,
        thirdPartyCookies: document.cookie !== undefined
      };
    } catch (error) {
      return {
        localStorage: false,
        sessionStorage: false,
        cookieEnabled: false,
        thirdPartyCookies: false,
        error: error.message
      };
    }
  },

  // Проверка сетевых возможностей
  checkNetworkCapabilities: () => {
    return {
      onLine: navigator.onLine,
      connection: ((): unknown => {
        const navWithConn = navigator as unknown as {
          connection?: BrowserNetworkInformation;
          mozConnection?: BrowserNetworkInformation;
          webkitConnection?: BrowserNetworkInformation;
        };
        return navWithConn.connection || navWithConn.mozConnection || navWithConn.webkitConnection;
      })(),
      serviceWorker: 'serviceWorker' in navigator,
      webRTC: ('RTCPeerConnection' in window) ||
        ('mozRTCPeerConnection' in (window as unknown as Record<string, unknown>)) ||
        ('webkitRTCPeerConnection' in (window as unknown as Record<string, unknown>))
    };
  },

  // Полная диагностика
  getFullDiagnostic: () => {
    const diagnostic = {
      timestamp: new Date().toISOString(),
      browser: browserDiagnostic.getBrowserInfo(),
      cors: browserDiagnostic.checkCORSSupport(),
      storage: browserDiagnostic.checkStorageSupport(),
      network: browserDiagnostic.checkNetworkCapabilities(),
      url: window.location.href,
      referrer: document.referrer,
      domain: document.domain,
      secure: window.location.protocol === 'https:'
    };

    console.log('🔍 [BROWSER DIAGNOSTIC] Full diagnostic:', diagnostic);
    return diagnostic;
  },

  // Тест прямого подключения к Supabase с performance timing
  testSupabaseConnectivity: async (supabaseUrl: string, apiKey: string) => {
    const testUrl = `${supabaseUrl}/rest/v1/`;
    
    try {
      console.log('🔍 [SUPABASE TEST] Testing connectivity to:', testUrl);
      
      // 📊 PERFORMANCE TIMING
      const start = performance.now();
      
      const response = await fetch(testUrl, {
        method: 'GET',
        headers: {
          'apikey': apiKey,
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      const duration = performance.now() - start;
      console.log(`🔍 [SUPABASE CONNECTIVITY] Duration: ${duration.toFixed(2)}ms`);

      const result = {
        success: response.ok,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        url: response.url,
        redirected: response.redirected,
        type: response.type,
        duration: duration
      };

      console.log('🔍 [SUPABASE TEST] Result:', result);
      return result;
    } catch (error: unknown) {
      const duration = performance.now() - (performance.now() - 5000); // Approximate
      
      // 🔍 РАСШИРЕННАЯ ОБРАБОТКА ОШИБОК
      let errorType = 'unknown';
      if (error instanceof Error && error.name === 'AbortError') {
        errorType = 'timeout';
        console.warn('🔍 [SUPABASE ERROR] Request aborted (timeout)');
      } else if (error instanceof Error && error.name === 'TypeError') {
        errorType = 'cors_or_network';
        console.error('🔍 [SUPABASE ERROR] Likely CORS or network issue:', error.message);
      } else {
        console.error('🔍 [SUPABASE ERROR] Unhandled error:', error);
      }
      
      const errorResult = {
        success: false,
        error: error instanceof Error ? error.message : String(error),
        name: error instanceof Error ? error.name : 'UnknownError',
        stack: error instanceof Error ? error.stack : undefined,
        errorType: errorType,
        duration: duration
      };
      
      console.error('🔍 [SUPABASE TEST] Error:', errorResult);
      return errorResult;
    }
  },

  // 🔍 RAW FETCH TEST к profiles таблице
  testRawProfilesFetch: async (supabaseUrl: string, apiKey: string, accessToken?: string, userId?: string) => {
    let profilesUrl = `${supabaseUrl}/rest/v1/profiles?select=id,user_id,full_name,email,role&limit=1`;
    if (userId) {
      profilesUrl += `&user_id=eq.${userId}`;
    }
    
    try {
      console.log('🔍 [RAW FETCH TEST] Testing profiles endpoint:', profilesUrl);
      
      // 📊 PERFORMANCE TIMING
      const start = performance.now();
      
      const headers: Record<string, string> = {
        'apikey': apiKey,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
      
      const response = await fetch(profilesUrl, {
        method: 'GET',
        headers: headers,
        credentials: 'include'
      });
      
      const duration = performance.now() - start;
      console.log(`🔍 [RAW FETCH] Duration: ${duration.toFixed(2)}ms`);
      
      let responseData;
      try {
        responseData = await response.json();
      } catch (jsonError) {
        responseData = await response.text();
      }
      
      const result = {
        success: response.ok,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        data: responseData,
        duration: duration,
        url: response.url
      };
      
      console.log('🔍 [RAW FETCH TEST] Result:', result);
      return result;
    } catch (error: unknown) {
      // 🔍 РАСШИРЕННАЯ ОБРАБОТКА ОШИБОК
      let errorType = 'unknown';
      if (error instanceof Error && error.name === 'AbortError') {
        errorType = 'timeout';
        console.warn('🔍 [RAW FETCH ERROR] Request aborted (timeout)');
      } else if (error instanceof Error && error.name === 'TypeError') {
        errorType = 'cors_or_network';
        console.error('🔍 [RAW FETCH ERROR] Likely CORS or network issue:', error.message);
      } else {
        console.error('🔍 [RAW FETCH ERROR] Unhandled error:', error);
      }
      
      const errorResult = {
        success: false,
        error: error instanceof Error ? error.message : String(error),
        name: error instanceof Error ? error.name : 'UnknownError',
        stack: error instanceof Error ? error.stack : undefined,
        errorType: errorType
      };
      
      console.error('🔍 [RAW FETCH TEST] Error:', errorResult);
      return errorResult;
    }
  }
};

// Автоматический запуск диагностики при загрузке
if (typeof window !== 'undefined') {
  // Запуск через небольшую задержку для полной инициализации
  setTimeout(() => {
    browserDiagnostic.getFullDiagnostic();
  }, 1000);
}
